import React, { useState } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  DollarSign,
  Eye,
  Package,
  Search,
  ShoppingCart,
  UserCheck,
  UserX,
  Users,
} from "lucide-react";

import { DashboardLayout } from "@/components/layout";
import {
  Button,
  Card,
  CardContent,
  ConfirmationModal,
  PageHeader,
  showToast,
  StatusBadge,
} from "@/components/ui";
import { withAppBase } from "@/lib/appPaths";
import { cn } from "@/lib/utils";
import { useAdminDashboard } from "@/hooks/useAdminDashboard";
import {
  setSellerStatus,
  useSellers,
  type SellerListItem,
} from "@/hooks/useSellers";
import { fmtVal } from "@/lib/orders";

const planColors: Record<string, string> = {
  FREE: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700",
  SILVER:
    "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600",
  GOLD: "bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800",
  PLATINUM:
    "bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800",
};

const statusToWorkspaceStatus: Record<string, string> = {
  All: "",
  Active: "ACTIVE",
  Suspended: "SUSPENDED",
  Closed: "CLOSED",
};

const syncStatusBadge: Record<string, string> = {
  HEALTHY: "ACTIVE",
  WARNING: "WARNING",
  FAILED: "FAILED",
  PAUSED: "PAUSED",
};

const workspaceStatusBadge: Record<string, string> = {
  ACTIVE: "ACTIVE",
  SUSPENDED: "BLOCKED",
  CLOSED: "DISABLED",
};

const iconBg: Record<string, string> = {
  indigo:
    "bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400",
  emerald:
    "bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400",
  rose: "bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400",
  amber: "bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400",
  blue: "bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400",
  teal: "bg-teal-50 dark:bg-teal-950 text-teal-600 dark:text-teal-400",
};

// Same pattern as the orders drawer's CopyBtn; stopPropagation because the row navigates.
const CopyBtn = ({ text }: { text: string }) => (
  <button
    data-testid={`copy-${text}`}
    title="Copy store ID"
    aria-label="Copy store ID"
    onClick={async (e) => {
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(text);
        showToast.info("Copied", text);
      } catch {
        // Denied permission or an insecure context: never claim a copy that did not happen.
        showToast.error("Copy failed", text);
      }
    }}
    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
  >
    <Copy className="w-3 h-3" />
  </button>
);

function avatarInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

const PAGE_SIZE = 10;

export default function Sellers() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(1);

  const { data: dash, loading: dashLoading } = useAdminDashboard();

  const {
    data: sellersData,
    loading: sellersLoading,
    refetch,
  } = useSellers({
    search: search || undefined,
    status: statusToWorkspaceStatus[statusFilter] || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const [pendingId, setPendingId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<SellerListItem | null>(null);
  const blocking = confirming?.status === "ACTIVE";

  async function toggleStatus(seller: SellerListItem) {
    const block = seller.status === "ACTIVE";
    const verb = block ? "Block" : "Unblock";
    setConfirming(null);
    setPendingId(seller.id);
    try {
      await setSellerStatus(seller.id, block ? "SUSPENDED" : "ACTIVE");
      await refetch();
      showToast.success(
        block ? `${seller.name} is blocked` : `${seller.name} is unblocked`,
      );
    } catch (err) {
      showToast.error(
        `${verb} failed`,
        err instanceof Error ? err.message : "Unknown error",
      );
    } finally {
      setPendingId(null);
    }
  }

  const kpiCards = [
    {
      key: "totalSellers",
      label: "Total Sellers",
      icon: Users,
      color: "indigo",
      value: dash?.sellers.totalCount ?? 0,
      delta: dash?.sellers.dayOnDayChangePct ?? 0,
      format: "number",
    },
    {
      key: "activeSellers",
      label: "Active Sellers",
      icon: UserCheck,
      color: "emerald",
      value: dash?.sellers.activeCount ?? 0,
      delta: dash?.sellers.dayOnDayChangePct ?? 0,
      format: "number",
    },
    {
      key: "blockedSellers",
      label: "Blocked Sellers",
      icon: UserX,
      color: "rose",
      value: dash?.sellers.suspendedCount ?? 0,
      delta: 0,
      format: "number",
    },
    {
      key: "totalActiveOrders",
      label: "Total Active Orders",
      icon: ShoppingCart,
      color: "amber",
      value: dash?.orders.pendingCount ?? 0,
      delta: dash?.orders.dayOnDayChangePct ?? 0,
      format: "number",
    },
    {
      key: "totalProducts",
      label: "Total Uploaded Products",
      icon: Package,
      color: "blue",
      value: dash?.listings.totalCount ?? 0,
      delta: dash?.listings.dayOnDayChangePct ?? 0,
      format: "number",
    },
    {
      key: "revenue7d",
      label: "Revenue (Last 7 Days)",
      icon: DollarSign,
      color: "teal",
      value: dash?.orders.sevenDayRevenue ?? 0,
      delta: dash?.orders.dayOnDayRevenueChangePct ?? 0,
      format: "currency",
    },
  ] as const;

  const items = sellersData?.items ?? [];
  const totalPages = sellersData?.totalPages ?? 1;
  const totalCount = sellersData?.totalCount ?? 0;

  return (
    <DashboardLayout activePath="/sellers">
      <PageHeader
        title="Sellers"
        subtitle="Monitor seller performance, activity, and operational health."
        breadcrumbs={[{ label: "Sellers", active: true }]}
      />

      {/* KPI Cards — sourced from adminDashboard */}
      <div
        data-testid="sellers-kpi-grid"
        className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8"
      >
        {kpiCards.map((kpi) => {
          const Icon = kpi.icon;
          const isUp = kpi.delta >= 0;
          const displayValue =
            kpi.format === "currency"
              ? fmtVal(kpi.value, "currency")
              : kpi.value.toLocaleString();

          return (
            <Card key={kpi.key} data-testid={`kpi-${kpi.key}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className={cn("p-2 rounded-lg", iconBg[kpi.color])}>
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <span
                    className={cn(
                      "inline-flex items-center gap-0.5 text-xs font-medium",
                      isUp
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-rose-600 dark:text-rose-400",
                    )}
                  >
                    {isUp ? (
                      <ArrowUpRight className="w-3 h-3" />
                    ) : (
                      <ArrowDownRight className="w-3 h-3" />
                    )}
                    {`${isUp ? "+" : ""}${kpi.delta.toFixed(1)}%`}
                  </span>
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {dashLoading ? "—" : displayValue}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {kpi.label}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div
        data-testid="sellers-filters"
        className="flex flex-wrap items-center gap-3 mb-6"
      >
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="sellers-search"
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by store name / owner name / email / phone / store ID"
            className={cn(
              "w-full pl-10 pr-4 py-2 text-sm rounded-lg",
              "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700",
              "text-slate-900 dark:text-slate-100 placeholder:text-slate-400",
              "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500",
            )}
          />
        </div>
        <div className="relative">
          <select
            data-testid="status-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className={cn(
              "appearance-none pl-3 pr-8 py-2 text-sm rounded-lg",
              "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700",
              "text-slate-900 dark:text-slate-100",
              "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500",
            )}
          >
            {["All", "Active", "Suspended", "Closed"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {sellersLoading ? "…" : `${totalCount} sellers`}
        </span>
      </div>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table data-testid="sellers-table" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                {[
                  "Seller",
                  "Workspace",
                  "Plan",
                  "Products",
                  "Orders",
                  "Revenue (30d)",
                  "Inventory",
                  "Sync",
                  "Status",
                  "",
                ].map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {sellersLoading ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-12 text-center text-slate-400 dark:text-slate-500"
                  >
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-12 text-center text-slate-400 dark:text-slate-500"
                  >
                    No sellers match your filters
                  </td>
                </tr>
              ) : (
                items.map((s) => (
                  <tr
                    key={s.id}
                    data-testid={`seller-row-${s.id}`}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                    onClick={() => {
                      window.location.href = withAppBase(`/sellers/${s.id}`);
                    }}
                  >
                    {/* Seller */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-xs font-bold text-indigo-700 dark:text-indigo-300 shrink-0">
                          {avatarInitials(s.name)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-slate-100">
                            {s.name}
                          </p>
                          <p className="text-xs text-slate-400 dark:text-slate-500">
                            {s.ownerEmail ?? "—"}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Workspace ID */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span
                          title={s.id}
                          className="font-mono text-xs text-slate-500 dark:text-slate-400"
                        >
                          {s.id.slice(0, 12)}…
                        </span>
                        <CopyBtn text={s.id} />
                      </div>
                    </td>

                    {/* Plan */}
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "px-2 py-0.5 text-[11px] font-semibold rounded-full border",
                          planColors[s.plan] ?? planColors["FREE"],
                        )}
                      >
                        {s.plan.charAt(0) + s.plan.slice(1).toLowerCase()}
                      </span>
                    </td>

                    {/* Products */}
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                      {s.totalProducts.toLocaleString()}
                    </td>

                    {/* Active Orders */}
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                      {s.activeOrders.toLocaleString()}
                    </td>

                    {/* Revenue 30d */}
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                      {fmtVal(s.revenue30d, "currency")}
                    </td>

                    {/* Inventory */}
                    <td className="px-4 py-3">
                      {s.lowStockCount > 0 ? (
                        <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                          {s.lowStockCount} low
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">OK</span>
                      )}
                    </td>

                    {/* Sync */}
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={syncStatusBadge[s.syncStatus] ?? s.syncStatus}
                        showDot
                      />
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={workspaceStatusBadge[s.status] ?? s.status}
                        showDot
                      />
                    </td>

                    {/* Action */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`view-seller-${s.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = withAppBase(
                              `/sellers/${s.id}`,
                            );
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {/* CLOSED is an end state, not a block: reopening it is not this action. */}
                        <Button
                          variant="ghost"
                          size="sm"
                          title={
                            s.status === "CLOSED"
                              ? "Closed accounts cannot be reopened here"
                              : s.status === "ACTIVE"
                                ? "Block this seller"
                                : "Unblock this seller"
                          }
                          disabled={pendingId === s.id || s.status === "CLOSED"}
                          data-testid={`toggle-seller-${s.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirming(s);
                          }}
                        >
                          {s.status === "ACTIVE" ? (
                            <UserX className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                          ) : (
                            <UserCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1 || sellersLoading}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages || sellersLoading}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <ConfirmationModal
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
        variant={blocking ? "danger" : "primary"}
        eyebrow={confirming?.ownerEmail ?? undefined}
        title={`${blocking ? "Block" : "Unblock"} ${confirming?.name ?? ""}?`}
        description={
          blocking
            ? "They will be signed out and unable to sign in, and their marketplace syncs will stop until you unblock them."
            : "They will be able to sign in again and their marketplace syncs will resume."
        }
        confirmLabel={blocking ? "Block seller" : "Unblock seller"}
        onConfirm={() => confirming && void toggleStatus(confirming)}
      />
    </DashboardLayout>
  );
}
